import { useEffect, useState } from 'react';
import { useMutation } from '@tanstack/react-query';

import BackendService, {
  GeoLocationData,
} from '../services/backendService/backendService';
import { useMetrics } from './useMetrics';

interface UseGeoLocationResult {
  country: string | null;
  isLoading: boolean;
  error: string | null;
  isUS: boolean;
}

/**
 * Custom hook to fetch the user's geolocation based on their IP address
 * Uses the backend /v0.5/geoip endpoint to determine the user's country
 */
export const useGeoLocation = (): UseGeoLocationResult => {
  const [country, setCountry] = useState<string | null>(null);
  const metrics = useMetrics(true);

  const { mutateAsync: fetchGeoLocation, isLoading, error } = useMutation(
    async () => {
      const data = await BackendService.instance.geoip.getLocation();
      return data;
    },
    {
      onSuccess: (data: GeoLocationData) => {
        if (data.country) {
          setCountry(data.country);
          metrics.logEvent('GeoLocation.Detected', {
            ipCountry: data.country,
          });
        }
      },
      onError: (err: Error) => {
        console.error('Failed to fetch geolocation:', err);
        // Default to null on error - this will show Individual Premium by default
        setCountry(null);
      },
    },
  );

  useEffect(() => {
    fetchGeoLocation();
  }, [fetchGeoLocation]);

  return {
    country,
    isLoading,
    error: error ? 'Failed to determine location' : null,
    isUS: country === 'US',
  };
};

export default useGeoLocation;
